import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { ButtonComponent, ButtonType } from '@app/components/button';
import { ChipColor, ChipComponent } from '@app/components/chip';
import { ConfirmDialogComponent } from '@app/components/confirm-dialog';
import { HeadingComponent } from '@app/components/heading';
import { InputIcon } from '@app/components/input';
import { SpinnerComponent } from '@app/components/spinner';
import { ColumnType, TableComponent, TableConfig } from '@app/components/table';
import { TextAlign } from '@app/components/text';
import {
  TextInputComponent,
  TextInputSize,
  TextInputType,
} from '@app/components/text-input';
import { ToastService, ToastType } from '@app/components/toast';
import { ViewAlign, ViewComponent, ViewJustify } from '@app/components/view';
import { AdminTenantService } from '@app/services/admin-tenant/admin-tenant.service';
import {
  AdminUserService,
  UserPermission,
} from '@app/services/admin-user/admin-user.service';
import { AdminUsersService } from '@app/services/admin-users/admin-users.service';
import { TranslatorService } from '@app/services/translator/translator.service';
import { Spacing } from '@app/shared/types';
import { environment } from '@environments/_current/environment';
import { TranslatePipe } from '@ngx-translate/core';
import { AaptivRoles, User } from '@skyfit/core-data-library';
import { UserStatusV2 } from '@skyfit/core-data-library/models/users-aaptiv-v2';
import { AddUsersDialogComponent } from './add-users/add-users.component';
import { EligibilityFileComponent } from './eligibility-file/eligibility-file.component';
import { InviteUsersComponent } from './invite-users/invite-users.component';

type SelectedUser = {
  id: string;
  name: string;
};

@Component({
  standalone: true,
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrl: './users.component.css',
  imports: [
    CommonModule,
    ButtonComponent,
    ChipComponent,
    ConfirmDialogComponent,
    TableComponent,
    SpinnerComponent,
    HeadingComponent,
    InviteUsersComponent,
    TableComponent,
    TranslatePipe,
    TextInputComponent,
    ViewComponent,
    EligibilityFileComponent,
    AddUsersDialogComponent,
  ],
})
export class UsersComponent implements OnInit {
  private readonly adminUsersService = inject(AdminUsersService);
  private readonly adminTenantService = inject(AdminTenantService);
  private readonly translatorService = inject(TranslatorService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  adminUserService = inject(AdminUserService);

  isLoading = signal(true);
  searchInput = signal('');
  statusFilter = signal<UserStatusV2 | null>(null);
  dialogOpen = signal(false);
  inviteUsersDialogOpen = signal(false);
  private readonly usersList = signal([]);
  filteredUsers = computed(() => {
    const searchInput = this.searchInput();
    const statusFilter = this.statusFilter();
    return this.usersList().filter((u) => {
      const matchesSearch =
        !searchInput ||
        this.isMatch(searchInput, u.name) ||
        this.isMatch(searchInput, u.email);
      const matchesStatus = !statusFilter || u._statusRaw === statusFilter;
      return matchesSearch && matchesStatus;
    });
  });
  readonly statusOptions: { label: string; value: UserStatusV2 | null }[] = [
    { label: 'common.all', value: null },
    { label: 'users.active', value: UserStatusV2.Active },
    { label: 'users.status.pending', value: UserStatusV2.Pending },
  ];
  private currentUser: User;
  readonly includedRoles = [
    { value: '', label: 'common.all' },
    { value: AaptivRoles.superadmin, label: 'common.admin' },
    { value: AaptivRoles.admin, label: 'common.enterpriseAdmin' },
    { value: AaptivRoles.client, label: 'common.client' },
    { value: AaptivRoles.trainer, label: 'common.trainer' },
  ];
  readonly tableConfig: TableConfig = {
    type: 'users.users',
    columns: [
      { key: 'name', label: 'users.name', maxLength: 30, sortable: true },
      { key: 'email', label: 'users.email', maxLength: 40, sortable: true },
      {
        key: 'status',
        label: 'users.statusLabel',
        columnType: ColumnType.chip,
        sortable: true,
      },
      { key: 'options', label: '', columnType: ColumnType.options },
    ],
  };

  selectedUser!: SelectedUser;
  roleFilter = '';
  InputIcon = InputIcon;
  InputSize = TextInputSize;
  InputType = TextInputType;
  ViewJustify = ViewJustify;
  ViewAlign = ViewAlign;
  ButtonType = ButtonType;
  Spacing = Spacing;
  TextAlign = TextAlign;
  tenantId = '';
  UserPermission = UserPermission;
  UserStatusV2 = UserStatusV2;

  setStatusFilter(value: UserStatusV2 | null): void {
    this.statusFilter.set(value);
  }

  ngOnInit(): void {
    this.route.parent.data
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        const { user } = data.initialData;
        this.currentUser = user;
      });
    this.getUsers();
  }

  onOptionSelected(event): void {
    this.selectedUser = {
      id: event.userId,
      name: event.name,
    };
    this.dialogOpen.set(true);
  }

  clearSelecteUser() {
    this.selectedUser = {
      id: '',
      name: '',
    };
  }

  closeDialog(): void {
    this.clearSelecteUser();
    this.dialogOpen.set(false);
  }

  handleInviteUsersDialog(): void {
    this.inviteUsersDialogOpen.set(!this.inviteUsersDialogOpen());
  }

  getUsers(): void {
    const readUserOption = this.adminUserService.hasPermission(
      UserPermission.READ_USER_OPTIONS
    );
    this.adminUsersService.getUserFromTenant().subscribe({
      next: (res) => {
        this.usersList.set(
          res.users.map((user) => {
            const showDeleteOption =
              this.currentUser &&
              user.email !== this.currentUser.email &&
              readUserOption &&
              user.status !== UserStatusV2.Pending;

            return {
              ...user,
              roles: user.roles?.filter((r) => this.isIncludedRole(r)),
              _statusRaw: user.status,
              status:
                user.status === UserStatusV2.Active
                  ? this.translatorService.translate('users.active')
                  : this.translatorService.translate('users.status.pending'),
              options: showDeleteOption
                ? [{ key: 'delete', label: 'common.remove' }]
                : null,
              chipColor:
                user.status === UserStatusV2.Active
                  ? ChipColor.NAVY
                  : ChipColor.BLUE,
            };
          })
        );
      },
      error: (error) => {
        console.error(error);
      },
      complete: () => {
        this.isLoading.set(false);
      },
    });
  }

  removeUser() {
    const currentUserId = this.selectedUser.id;
    this.tenantId = `${environment.defaultTenantId}`;
    this.adminUserService
      .addUserToTenant(currentUserId, this.tenantId)
      .subscribe({
        error: () => {
          const message = this.translatorService.translate(
            'client.removeUserError',
            { name: this.selectedUser.name }
          );
          this.toastService.add(message, ToastType.ERROR);
        },
        complete: () => {
          const message = this.translatorService.translate(
            'client.removeUserSuccess',
            { name: this.selectedUser.name }
          );
          this.toastService.add(message, ToastType.INFO);
          this.closeDialog();
          this.getUsers();
        },
      });
  }

  private isIncludedRole(role: string): boolean {
    return this.includedRoles.some((ir) => {
      return ir.value.toLocaleLowerCase() === role.toLocaleLowerCase();
    });
  }

  private isMatch(input?: string, value?: string): boolean {
    return (
      input && value?.toLocaleLowerCase()?.includes(input?.toLocaleLowerCase())
    );
  }

  handleDownloadEmails(): void {
    const usersList = this.usersList();

    if (usersList.length === 0) {
      this.toastService.add('No users to download', ToastType.ERROR);
      return;
    }
    try {
      this.adminUsersService.downloadEmailsCSV(usersList);
      this.toastService.add('Users downloaded successfully', ToastType.INFO);
    } catch (error) {
      this.toastService.add('Failed to download users', ToastType.ERROR);
    }
  }
}
